interface ScoreGaugeProps {
  score: number;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'High';
  if (score >= 60) return 'Medium-High';
  if (score >= 40) return 'Medium';
  return 'Low';
}

const sizeConfig = {
  sm: { size: 80, strokeWidth: 6, fontSize: 'text-lg' },
  md: { size: 120, strokeWidth: 8, fontSize: 'text-2xl' },
  lg: { size: 160, strokeWidth: 10, fontSize: 'text-3xl' },
};

export function ScoreGauge({ score, size = 'md', showLabel = true }: ScoreGaugeProps) {
  const config = sizeConfig[size];
  const radius = (config.size - config.strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  const label = getScoreLabel(score);

  const scoreColor =
    score >= 80
      ? 'hsl(var(--success) / 0.5)'
      : score >= 50
        ? 'hsl(var(--warning) / 0.5)'
        : 'hsl(var(--destructive) / 0.5)';

  const colorClass =
    score >= 80 ? 'text-success' : score >= 50 ? 'text-warning' : 'text-destructive';

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: config.size, height: config.size }}>
        <svg
          className="transform -rotate-90"
          width={config.size}
          height={config.size}
          role="img"
          aria-label={`Propensity score: ${score} out of 100, rated ${label}`}
        >
          <circle
            cx={config.size / 2}
            cy={config.size / 2}
            r={radius}
            stroke="hsl(var(--muted) / 0.3)"
            strokeWidth={config.strokeWidth}
            fill="none"
          />
          <circle
            cx={config.size / 2}
            cy={config.size / 2}
            r={radius}
            stroke={scoreColor}
            className="transition-all duration-500 ease-out"
            strokeWidth={config.strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          aria-hidden="true"
        >
          <span className={`${config.fontSize} font-semibold font-tabular`}>{score}</span>
          <span className="text-xs text-muted-foreground">/ 100</span>
        </div>
      </div>
      {showLabel && (
        <span className={`mt-2 text-sm font-medium ${colorClass}`}>{label} Propensity</span>
      )}
    </div>
  );
}
